
import { supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";
import { Payment } from "../types";
import { PaymentCacheService } from "./paymentCacheService";
import { mapToPayment } from "./paymentUtils";

/**
 * Service for retrieving organization payments
 */
export const OrganizationPaymentService = {
  /**
   * Get all payments for an organization with pagination and filtering
   */
  getPaymentsForOrganization: async (
    organizationId: string,
    page: number = 1,
    pageSize: number = 20,
    startDate?: string,
    endDate?: string,
    status?: string,
    paymentMethod?: string
  ): Promise<{data: Payment[], totalCount: number}> => {
    const context: ErrorContext = {
      operation: "getPaymentsForOrganization",
      organizationId,
    };

    try {
      // Create cache key based on parameters
      const cacheKey = `org:${organizationId}:${page}:${pageSize}:${startDate || ''}:${endDate || ''}:${status || ''}:${paymentMethod || ''}`;
      const cachedData = PaymentCacheService.get(cacheKey);
      if (cachedData) {
        return { data: cachedData, totalCount: cachedData.length };
      }
      
      // Count query with filters
      let countQuery = supabase
        .from('payments')
        .select('*', { count: 'exact', head: true })
        .eq("organization_id", organizationId);
      
      // Apply filters to count query
      if (startDate) {
        countQuery = countQuery.gte("paid_at", startDate);
      }
      if (endDate) {
        countQuery = countQuery.lte("paid_at", endDate);
      }
      if (status) {
        countQuery = countQuery.eq("status", status);
      }
      if (paymentMethod) {
        countQuery = countQuery.eq("payment_method", paymentMethod);
      }
      
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;
      
      // Calculate pagination values
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Main data query with same filters
      let query = supabase
        .from('payments')
        .select(`
          *,
          families:family_id(name)
        `);
      
      // Apply filters
      query = query.eq("organization_id", organizationId);
      
      if (startDate) {
        query = query.gte("paid_at", startDate);
      }
      if (endDate) {
        query = query.lte("paid_at", endDate);
      }
      if (status) {
        query = query.eq("status", status);
      }
      if (paymentMethod) {
        query = query.eq("payment_method", paymentMethod);
      }
      
      // Apply pagination and sorting
      query = query
        .order("paid_at", { ascending: false })
        .range(from, to);

      const { data, error } = await query;

      if (error) throw error;
      
      // Transform payments to include family name
      const payments = data ? data.map(record => {
        const payment = mapToPayment(record);
        payment.family_name = record.families?.name || 'Unknown Family';
        return payment;
      }) : [];
      
      // Cache the result
      PaymentCacheService.set(cacheKey, payments);
      
      return {
        data: payments,
        totalCount: count || 0
      };
    } catch (error) {
      handleError(
        error,
        "Failed to load payments",
        ErrorSeverity.ERROR,
        context
      );
      return { data: [], totalCount: 0 };
    }
  },

  /**
   * Get a specific payment by ID
   */
  getPaymentById: async (
    paymentId: string
  ): Promise<Payment | null> => {
    const context: ErrorContext = {
      operation: "getPaymentById",
      additionalInfo: { paymentId },
    };

    try {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          families:family_id(name)
        `)
        .eq("id", paymentId)
        .single();

      if (error) throw error;
      
      if (!data) return null;
      
      const payment = mapToPayment(data);
      payment.family_name = data.families?.name || 'Unknown Family';
      
      return payment;
    } catch (error) {
      handleError(
        error,
        "Failed to load payment",
        ErrorSeverity.ERROR,
        context
      );
      return null;
    }
  }
};
